import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { pushTaskStatusPopup } from 'app/src/shared/ui/task-status-popups/task-status-popups-store';
import { refreshSidebarUnreadBadges } from 'app/src/shared/sidebar-unread-badges/sidebar-unread-badges-bootstrap';
import { areWsNotificationsToastsEnabled } from './ws-notifications-preference';

export type NotificationToastType = 'success' | 'error' | 'attention' | 'information';

export type ShowMessageFn = (
  title: string,
  message: string,
  type: NotificationToastType,
) => void;

export interface NotificationsHubConnectOptions {
  hubUrl: string;
  accessToken: string;
  showMessage: ShowMessageFn;
}

let connection: HubConnection | null = null;
/** Совпадение URL+токена — повторный connect не нужен. */
let activeSessionKey: string | null = null;

function sessionKey(hubUrl: string, accessToken: string): string {
  return `${ hubUrl }\n${ accessToken }`;
}

/**
 * Как в тестовом клиенте бэкенда (SignalR): WebSocket сразу на `/hub/...`,
 * без negotiate; токен в query `access_token` и через factory (на переподключение).
 */
function hubUrlWithAccessTokenQuery(hubUrl: string, accessToken: string): string {
  const u = new URL(hubUrl);
  u.searchParams.set('access_token', accessToken);
  return u.toString();
}

/** Сырой кадр протокола SignalR: `{ type, target, arguments: [...] }` или уже первый аргумент хаба. */
function unwrapHubInvocationPayload(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object') {
    return raw;
  }
  const r = raw as Record<string, unknown>;
  if (Array.isArray(r.arguments) && r.arguments.length > 0) {
    return r.arguments[ 0 ];
  }
  return raw;
}

function toLowerKeyMap(obj: Record<string, unknown>): Record<string, unknown> {
  const lower: Record<string, unknown> = {};
  for (const [ k, v ] of Object.entries(obj)) {
    lower[ k.toLowerCase() ] = v;
  }
  return lower;
}

const ZERO_GUID = /^0{8}-0{4}-0{4}-0{4}-0{12}$/i;

function pickDescription(lower: Record<string, unknown>): string {
  for (const key of [ 'description', 'message', 'text', 'body', 'content' ]) {
    const v = lower[ key ];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
}

function pickTitle(lower: Record<string, unknown>): string {
  for (const key of [ 'title', 'subject' ]) {
    const v = lower[ key ];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return 'Уведомление';
}

function formatNotificationMessage(lower: Record<string, unknown>): string {
  const main = pickDescription(lower);
  const meta: string[] = [];

  const created = lower.createdat;
  if (created != null && String(created).trim() !== '') {
    const d = new Date(String(created));
    meta.push(
      Number.isNaN(d.getTime())
        ? `Дата: ${ String(created) }`
        : `Дата: ${ d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) }`,
    );
  }

  const op = lower.authorizationoperation;
  if (typeof op === 'string' && op.trim()) {
    meta.push(`Операция: ${ op.trim() }`);
  }

  const uid = lower.userid;
  if (typeof uid === 'string' && uid.trim() && !ZERO_GUID.test(uid.trim())) {
    meta.push(`Пользователь: ${ uid.trim() }`);
  }

  if (import.meta.env.DEV) {
    const id = lower.id;
    if (typeof id === 'string' && id.trim()) {
      meta.push(`Идентификатор: ${ id.trim() }`);
    }
    if (lower.isread === false) {
      meta.push('Статус: не прочитано');
    }
  }

  if (main && meta.length > 0) {
    return `${ main }\n\n${ meta.join('\n') }`;
  }
  if (main) {
    return main;
  }
  if (meta.length > 0) {
    return meta.join('\n');
  }

  const entries = Object.entries(lower).filter(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([ _, v ]) => v != null && v !== '',
  );
  if (entries.length === 0) {
    return '';
  }
  return entries
    .map(([ k, v ]) => `${ k }: ${ typeof v === 'object' ? JSON.stringify(v) : String(v) }`)
    .join('\n');
}

function normalizePayload(raw: unknown): { title: string; message: string } {
  const data = unwrapHubInvocationPayload(raw);

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const lower = toLowerKeyMap(data as Record<string, unknown>);
    const title = pickTitle(lower);
    const message = formatNotificationMessage(lower);
    return {
      title,
      message: message || 'Без текста',
    };
  }

  const text = typeof data === 'string' ? data : JSON.stringify(data);
  return { title: 'Уведомление', message: text };
}

function tryHandleTaskStatusChanged(payload: unknown): boolean {
  const data = unwrapHubInvocationPayload(payload);
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  const lower = toLowerKeyMap(data as Record<string, unknown>);

  const operationRaw = lower.operation;
  const operation = typeof operationRaw === 'string' ? operationRaw.trim() : '';

  if (operation === 'TaskOverdue') {
    pushTaskStatusPopup('overdue');
    return true;
  }

  if (operation !== 'TaskStatusChanged') {
    return false;
  }

  const description = pickDescription(lower);
  if (!description) {
    return false;
  }

  const m = description.match(/Статус задачи изменен на:\s*([^.\s]+)\s*\.?/i);
  const status = (m?.[ 1 ] || '').trim();
  if (!status) {
    return false;
  }

  if (status === 'overdue') {
    pushTaskStatusPopup('overdue');
    return true;
  }
  if (status === '9516f9dd-95ff-4377-9d91-d987c457eb5c') {
    pushTaskStatusPopup('closedInTime');
    return true;
  }
  return false;
}

export async function connectNotificationsHub(
  options: NotificationsHubConnectOptions,
): Promise<void> {
  const { hubUrl, accessToken, showMessage } = options;
  const key = sessionKey(hubUrl, accessToken);
  if (
    connection
    && activeSessionKey === key
    && connection.state === HubConnectionState.Connected
  ) {
    return;
  }

  await disconnectNotificationsHub();

  const urlWithToken = hubUrlWithAccessTokenQuery(hubUrl, accessToken);

  connection = new HubConnectionBuilder()
    .withUrl(urlWithToken, {
      accessTokenFactory: () => accessToken,
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
    })
    .configureLogging(LogLevel.Information)
    .withAutomaticReconnect([ 0, 2000, 10_000, 30_000 ])
    .build();

  const onHubPayload = (payload: unknown) => {
    refreshSidebarUnreadBadges();

    if (!areWsNotificationsToastsEnabled()) {
      return;
    }
    if (tryHandleTaskStatusChanged(payload)) {
      return;
    }
    const { title, message } = normalizePayload(payload);
    showMessage(title, message, 'information');
  };

  connection.on('ReceiveNotification', onHubPayload);
  connection.on('ReceiveAllEvent', onHubPayload);

  connection.onreconnected(() => {
    if (areWsNotificationsToastsEnabled()) {
      showMessage('Уведомления', 'Соединение восстановлено', 'success');
    }
  });

  try {
    if (import.meta.env.DEV) {
      console.info('[notifications-hub] SignalR (WS, skip negotiate):', hubUrl);
    }
    await connection.start();
    activeSessionKey = key;
  } catch (err) {
    console.error('Ошибка подключения к hub уведомлений:', err);
    try {
      await connection.stop();
    } catch {
      /* noop */
    }
    connection = null;
    activeSessionKey = null;
    throw err;
  }
}

export async function disconnectNotificationsHub(): Promise<void> {
  if (!connection) {
    return;
  }
  try {
    await connection.stop();
  } catch {
    /* noop */
  }
  connection = null;
  activeSessionKey = null;
}
